// @ts-check
// Indicação do CEP no card (CEP na família, pacotes 1.12.0 e 1.12.1, contrato 07, seção 9.9): o card não tem endereço
// próprio; aqui aparece, somente leitura e logo depois da Etapa, o CEP da família que o mapa de calor usa para este
// lead (só o da família: o do contato principal não conta) e o link para informar ou corrigir na família, que abre no
// painel lateral com a linha "CEP" editável. Uma leitura da família ao abrir o card (nunca nas listas); o estado do
// CEP (não encontrado) vem da tabela de CEPs da escola.
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { createLiveRecordList } from '../../data/record-store.js';
import { findOneRecord } from '../../data/rest.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { describeLeadPostcode, splitAroundPostcode } from '../../lib/address-sub-fields.js';
import { readPostcodeStatus } from '../../data/postcode-statuses.js';
import { openRecord } from '../side-panel/open-record.js';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';

const HOUSEHOLD_ADDRESS_FIELD = 'householdAddress';

/**
 * @param {{ record: Record<string, any> }} props card (enrollmentOpportunity) com householdId
 */
export function FamilyPostcodeHint({ record }) {
  const householdObject = objectsByNameSingular.value.get('household');
  // o papel que não lê o endereço da família não vê o CEP em lugar nenhum: nem a indicação
  const canReadHousehold = Boolean(householdObject && canReadObjectRecords(householdObject)
    && householdObject.fields.some((field) => field.name === HOUSEHOLD_ADDRESS_FIELD));
  // lista viva: a edição do CEP no painel da família aparece aqui sem recarregar
  const householdList = useMemo(() => createLiveRecordList('household'), []);
  useEffect(() => () => householdList.dispose(), []);
  const [householdState, setHouseholdState] = useState(/** @type {'loading' | 'ready' | 'hidden'} */ ('loading'));
  const householdId = record?.householdId ?? null;

  useEffect(() => {
    let isCancelled = false;
    householdList.records.value = [];
    if (!canReadHousehold || !householdObject) return undefined;
    if (!householdId) {
      setHouseholdState('ready');
      return undefined;
    }
    setHouseholdState('loading');
    findOneRecord(householdObject, householdId, { depth: 0 })
      .then((household) => {
        if (isCancelled) return;
        householdList.records.value = household ? [household] : [];
        setHouseholdState(household ? 'ready' : 'hidden');
      })
      // 403 (escopo do papel) ou 404 (família na lixeira): some sem erro
      .catch(() => {
        if (!isCancelled) setHouseholdState('hidden');
      });
    return () => {
      isCancelled = true;
    };
  }, [householdId, canReadHousehold]);

  if (!canReadHousehold || householdState !== 'ready') return null;
  const household = householdList.records.value.find((candidate) => candidate.id === householdId) ?? null;
  const householdPostcodeStatus = householdId ? readPostcodeStatus(household?.[HOUSEHOLD_ADDRESS_FIELD]?.addressPostcode) : null;
  const description = describeLeadPostcode({ household: householdId ? household : null, householdPostcodeStatus });
  const text = description.text ?? t(description.textKey, description.textValues);
  const textParts = splitAroundPostcode(text, description.textValues.postcode ?? description.text);
  const label = t('familyPostcode.cardHint.label');
  // mesma grade das linhas de campo (rótulo à esquerda), somente leitura: o CEP se edita na família
  return html`<div class="inline-cell family-postcode-hint" data-readonly="true" data-testid="family-postcode-hint" data-state=${description.state} data-tone=${description.tone}>
    <div class="inline-cell-label" title=${label}>
      <${Icon} name="IconMapPin" size=${16} stroke=${1.6} />
      <span class="inline-cell-label-text">${label}</span>
    </div>
    <div class="family-postcode-hint-value">
      <span class="family-postcode-hint-text">${textParts.map((part) => (part.isPostcode
        ? html`<span class="family-postcode-hint-postcode">${part.text}</span>`
        : part.text))}</span>
      ${description.linkKey && householdId
        ? html`<button
            type="button"
            class="family-postcode-hint-link"
            onClick=${(/** @type {MouseEvent} */ event) => openRecord({
              objectNameSingular: 'household', recordId: householdId, openIn: 'SIDE_PANEL', source: /** @type {Element} */ (event.currentTarget),
            })}
          >${t(description.linkKey)}</button>`
        : null}
    </div>
  </div>`;
}
